import React from 'react';

import { Control, Controller, useController, useWatch } from 'react-hook-form';
import dayjs from '~/timeInit';

import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import tenants from '~/consts/tenant';

import { formatDateLongISO } from '~/services/TimeService';

export interface TagScheduleFormData {
  brands: Array<string>;
  activeStartDate: string | null;
  activeEndDate: string | null;
}

interface TagScheduleFormProps {
  control: Control<TagScheduleFormData>;
}

export default function TagScheduleForm({ control }: TagScheduleFormProps) {
  const activeStartDate = useWatch({ control, name: 'activeStartDate' });

  const {
    field: brandsField,
    fieldState: { error: brandsError },
  } = useController({
    control,
    name: 'brands',
    rules: { required: 'Please select at least one brand' },
  });

  return (
    <Stack spacing={2} mt={1}>
      <FormControl fullWidth error={!!brandsError}>
        <InputLabel>Brands</InputLabel>
        <Select
          multiple
          value={brandsField.value}
          onChange={brandsField.onChange}
          onBlur={brandsField.onBlur}
          inputRef={brandsField.ref}
          input={<OutlinedInput label="Brands" />}
          renderValue={(selected) => (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
          required
        >
          {Object.entries(tenants).map(([key, tenant]) => (
            <MenuItem key={key} value={tenant.brand}>
              <Checkbox checked={brandsField.value.includes(tenant.brand)} />
              <ListItemText primary={tenant.title} />
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{brandsError?.message}</FormHelperText>
      </FormControl>
      <Controller
        name="activeStartDate"
        control={control}
        rules={{ required: 'Please select active period start date' }}
        render={({ field, fieldState: { error } }) => {
          const dateValue = field.value ? dayjs(field.value) : null;
          return (
            <DatePicker
              label="Active Period Start"
              value={dateValue}
              onChange={(date) => field.onChange(date ? formatDateLongISO(date.toDate()) : null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!error,
                  helperText: error?.message,
                  onBlur: field.onBlur,
                },
              }}
            />
          );
        }}
      />
      <Controller
        name="activeEndDate"
        control={control}
        rules={{ required: 'Please select active period end date' }}
        render={({ field, fieldState: { error } }) => {
          const dateValue = field.value ? dayjs(field.value) : null;
          const minDate = activeStartDate ? dayjs(activeStartDate) : undefined;
          return (
            <DatePicker
              label="Active Period End"
              value={dateValue}
              onChange={(date) => field.onChange(date ? formatDateLongISO(date.toDate()) : null)}
              minDate={minDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!error,
                  helperText: error?.message,
                  onBlur: field.onBlur,
                },
              }}
            />
          );
        }}
      />
    </Stack>
  );
}
